/**
 * ExternalIntakeQueue - Sprint 11
 * PMO view for managing public/external demand submissions
 * Includes attachment viewing, download, and AI summary functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Inbox, CheckCircle, XCircle, RefreshCw, Clock, User, Mail,
  Building2, AlertTriangle, ExternalLink, Eye, ChevronRight,
  Paperclip, Download, FileText, FileImage, FileSpreadsheet,
  File, FileType, ChevronDown, ChevronUp, X, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import AIAttachmentSummary from '../components/AIAttachmentSummary';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// File type icons with tooltips
const getFileTypeLabel = (type) => {
  if (type?.includes('image')) return 'Image File';
  if (type?.includes('spreadsheet') || type?.includes('excel')) return 'Spreadsheet (Excel)';
  if (type?.includes('pdf')) return 'PDF Document';
  if (type?.includes('presentation') || type?.includes('powerpoint')) return 'Presentation (PowerPoint)';
  if (type?.includes('word') || type?.includes('document')) return 'Word Document';
  if (type?.includes('text')) return 'Text File';
  return 'File';
};

const getFileIcon = (type, withTooltip = false) => {
  let icon;
  if (type?.includes('image')) icon = <FileImage className="h-4 w-4 text-purple-500" />;
  else if (type?.includes('spreadsheet') || type?.includes('excel')) icon = <FileSpreadsheet className="h-4 w-4 text-green-500" />;
  else if (type?.includes('pdf')) icon = <FileType className="h-4 w-4 text-red-500" />;
  else if (type?.includes('presentation') || type?.includes('powerpoint')) icon = <FileText className="h-4 w-4 text-orange-500" />;
  else icon = <File className="h-4 w-4 text-blue-500" />;
  
  if (withTooltip) {
    const label = getFileTypeLabel(type);
    return (
      <div className="relative group" title={label}>
        {icon}
        <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-800 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
          {label}
        </span>
      </div>
    );
  }
  return icon;
};

// Format file size
const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const ExternalIntakeQueue = () => {
  const navigate = useNavigate();
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [expandedRow, setExpandedRow] = useState(null);
  const [downloadingFile, setDownloadingFile] = useState(null);
  const [intakeConfig, setIntakeConfig] = useState(null);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('simpliview_token');
    const tenantId = localStorage.getItem('impersonated_tenant_id') || localStorage.getItem('simpliview_user_tenant_id');
    const headers = {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }
    return headers;
  }, []);

  const fetchQueue = useCallback(async () => {
    try {
      // Fetch queue
      const response = await fetch(`${API_URL}/api/public/intake-queue`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setSubmissions(data.data || []);
      } else {
        toast.error('Failed to load intake queue');
      }
      
      // Fetch CAPTCHA config (public endpoint)
      const configResponse = await fetch(`${API_URL}/api/public/intake-config`);
      if (configResponse.ok) {
        const configData = await configResponse.json();
        setIntakeConfig(configData);
      }
    } catch (err) {
      console.error('Failed to fetch queue:', err);
      toast.error('Failed to load intake queue');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const handleAccept = async (submissionId) => {
    setProcessing(submissionId);
    try {
      const response = await fetch(`${API_URL}/api/public/intake-queue/${submissionId}/accept`, {
        method: 'POST',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const result = await response.json();
        const attachmentMsg = result.attachments_transferred > 0 
          ? ` (${result.attachments_transferred} attachments transferred)` 
          : '';
        toast.success(`Submission accepted and moved to intake${attachmentMsg}`);
        fetchQueue();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to accept submission');
      }
    } catch (err) {
      toast.error('Failed to accept submission');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (submissionId) => {
    const reason = window.prompt('Rejection reason (optional):');
    if (reason === null) return; // User cancelled

    setProcessing(submissionId);
    try {
      const response = await fetch(
        `${API_URL}/api/public/intake-queue/${submissionId}/reject?reason=${encodeURIComponent(reason)}`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );

      if (response.ok) {
        toast.success('Submission rejected');
        fetchQueue();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to reject submission');
      }
    } catch (err) {
      toast.error('Failed to reject submission');
    } finally {
      setProcessing(null);
    }
  };

  const handleDownload = async (submissionId, attachment) => {
    setDownloadingFile(attachment.fileId);
    try {
      const token = localStorage.getItem('simpliview_token');
      const response = await fetch(
        `${API_URL}/api/public/intake-queue/${submissionId}/attachments/${attachment.fileId}/download`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = attachment.name;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success(`Downloaded ${attachment.name}`);
      } else {
        toast.error('Failed to download file');
      }
    } catch (err) {
      console.error('Download failed:', err);
      toast.error('Failed to download file');
    } finally {
      setDownloadingFile(null);
    }
  };

  const toggleExpand = (submissionId) => {
    setExpandedRow(prev => prev === submissionId ? null : submissionId);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="external-intake-queue">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Inbox className="h-6 w-6" />
            External Intake Queue
          </h1>
          <p className="text-gray-500 mt-1 flex items-center gap-3">
            Review and process public demand submissions
            {/* CAPTCHA Status Chip */}
            {intakeConfig && (
              <Badge 
                variant={intakeConfig.captcha_enabled ? 'default' : 'secondary'}
                className={intakeConfig.captcha_enabled ? 'bg-green-600' : 'bg-gray-400'}
              >
                CAPTCHA: {intakeConfig.captcha_enabled ? 'Enabled (hCaptcha)' : 'Disabled'}
              </Badge>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchQueue}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button variant="outline" onClick={() => window.open('/public/demand-intake', '_blank')}>
            <ExternalLink className="h-4 w-4 mr-2" />
            View Public Form
          </Button>
        </div>
      </div>

      {/* Queue Stats */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <Card className="bg-gradient-to-br from-cyan-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-cyan-600 font-medium">Pending Review</p>
                <p className="text-2xl font-bold">{submissions.length}</p>
              </div>
              <div className="p-2 bg-cyan-100 rounded-lg">
                <Inbox className="h-5 w-5 text-cyan-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-yellow-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-yellow-600 font-medium">High Urgency</p>
                <p className="text-2xl font-bold">
                  {submissions.filter(s => s.urgency === 'high' || s.urgency === 'critical').length}
                </p>
              </div>
              <div className="p-2 bg-yellow-100 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-purple-600 font-medium">With Attachments</p>
                <p className="text-2xl font-bold">
                  {submissions.filter(s => s.attachment_count > 0).length}
                </p>
              </div>
              <div className="p-2 bg-purple-100 rounded-lg">
                <Paperclip className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-blue-50 to-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-blue-600 font-medium">Avg Wait Time</p>
                <p className="text-2xl font-bold">-</p>
              </div>
              <div className="p-2 bg-blue-100 rounded-lg">
                <Clock className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Submissions List */}
      {submissions.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <Inbox className="h-12 w-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-700">No pending submissions</h3>
            <p className="text-gray-500 mt-2">
              External submissions will appear here for review
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {submissions.map((submission) => (
            <Card 
              key={submission.id || submission.submission_id} 
              className="hover:shadow-md transition-shadow"
              data-testid={`submission-${submission.submission_id}`}
            >
              <CardContent className="py-4">
                {/* Main Row */}
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-lg">{submission.title}</h3>
                      <Badge className="bg-cyan-100 text-cyan-700">
                        {submission.submission_id}
                      </Badge>
                      <Badge 
                        className={
                          submission.urgency === 'critical' ? 'bg-red-100 text-red-700' :
                          submission.urgency === 'high' ? 'bg-orange-100 text-orange-700' :
                          submission.urgency === 'medium' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-700'
                        }
                      >
                        {submission.urgency}
                      </Badge>
                      {submission.attachment_count > 0 && (
                        <Badge 
                          className="bg-purple-100 text-purple-700 cursor-pointer"
                          onClick={() => toggleExpand(submission.submission_id)}
                        >
                          <Paperclip className="h-3 w-3 mr-1" />
                          {submission.attachment_count} file{submission.attachment_count > 1 ? 's' : ''}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Description preview */}
                    {submission.description && (
                      <p className="text-sm text-gray-600 mb-3 line-clamp-2">
                        {submission.description}
                      </p>
                    )}
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <span>{submission.requestor_name}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="truncate">{submission.requestor_email}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        <span>{submission.department}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span>{submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : '-'}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 ml-4">
                    {submission.attachment_count > 0 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleExpand(submission.submission_id)}
                        data-testid={`expand-${submission.submission_id}`}
                      >
                        {expandedRow === submission.submission_id ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/demands/${submission.id}`)}
                      disabled={!submission.id}
                    >
                      <Eye className="h-4 w-4 mr-1" />
                      View
                    </Button>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={() => handleAccept(submission.submission_id)}
                      disabled={processing !== null}
                      data-testid={`accept-${submission.submission_id}`}
                    >
                      {processing === submission.submission_id ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Accept
                        </>
                      )}
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleReject(submission.submission_id)}
                      disabled={processing !== null}
                      data-testid={`reject-${submission.submission_id}`}
                    >
                      <XCircle className="h-4 w-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
                
                {/* Expanded Attachments Section */}
                {expandedRow === submission.submission_id && submission.attachments?.length > 0 && (
                  <div className="mt-4 pt-4 border-t" data-testid={`attachments-${submission.submission_id}`}>
                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                      <Paperclip className="h-4 w-4" />
                      Attached Files ({submission.attachments.length})
                    </h4>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                      {submission.attachments.map((att, idx) => (
                        <div 
                          key={att.fileId || idx}
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                        >
                          <div className="flex items-center gap-3">
                            {getFileIcon(att.type, true)}
                            <div>
                              <p className="text-sm font-medium text-gray-700 truncate max-w-[200px]">
                                {att.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                {formatFileSize(att.size)} 
                                {att.uploadedAt && ` • ${new Date(att.uploadedAt).toLocaleDateString()}`}
                              </p>
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDownload(submission.submission_id, att)}
                            disabled={downloadingFile === att.fileId}
                            data-testid={`download-${att.fileId}`}
                          >
                            {downloadingFile === att.fileId ? (
                              <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                              <Download className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                    
                    {/* AI Summary Panel */}
                    <AIAttachmentSummary 
                      submissionId={submission.submission_id}
                      attachmentCount={submission.attachment_count}
                      onSummaryAccepted={() => {
                        toast.success('AI summary added to demand');
                        fetchQueue();
                      }}
                    />
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Info Card */}
      <Card className="mt-6 bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <h4 className="font-medium text-blue-800 mb-2">External Intake Process</h4>
          <div className="flex items-center gap-4 text-sm text-blue-700">
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 rounded-full bg-cyan-500 text-white flex items-center justify-center text-xs">1</div>
              <span>External Submit</span>
            </div>
            <ChevronRight className="h-4 w-4" />
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 rounded-full bg-yellow-500 text-white flex items-center justify-center text-xs">2</div>
              <span>PMO Review</span>
            </div>
            <ChevronRight className="h-4 w-4" />
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 rounded-full bg-green-500 text-white flex items-center justify-center text-xs">3</div>
              <span>Accept to Intake</span>
            </div>
            <ChevronRight className="h-4 w-4" />
            <div className="flex items-center gap-1">
              <div className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-xs">4</div>
              <span>Normal Workflow</span>
            </div>
          </div>
          <p className="text-xs text-blue-600 mt-3">
            Attachments are automatically transferred when submissions are accepted. 
            Supported documents (PDF, DOCX, XLSX, PPTX) will be queued for AI extraction.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default ExternalIntakeQueue;
